import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { coachQueryOptions } from "~/lib/query-options";
import type { CoachOverview } from "~/lib/coach-grouping";
import { getCategoryBySlug } from "~/lib/functions/expertise";
import { getCityLabelFromValue } from "~/lib/functions/cities";
import { getCityCategoryPageContent } from "~/lib/functions/coach-group-seo";
import { Heading } from "~/components/ui/heading";
import { CoachCard } from "~/components/coach-card";
import { BreakdownIntro } from "~/components/feature/breakdown-intro";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { MarkdownContent } from "~/components/markdown-content";
import { FaqList } from "~/components/faq-list";
import { BlogGrid } from "~/components/blog-grid";
import { createMetaTags } from "~/lib/shared/meta";

export const Route = createFileRoute(
  "/_public/coaches/city/$slug/category/$categorySlug/",
)({
  loader: async ({ context, params }) => {
    const { slug: citySlug, categorySlug } = params;
    const [coaches, category, cityLabel, page] = await Promise.all([
      context.queryClient.ensureQueryData(
        coachQueryOptions.byCategoryAndCity(categorySlug, citySlug),
      ),
      getCategoryBySlug({ data: { slug: categorySlug } }),
      getCityLabelFromValue({ data: { citySlug } }),
      getCityCategoryPageContent({ data: { citySlug, categorySlug } }),
    ]);

    await context.queryClient.ensureQueryData(
      coachQueryOptions.byCategoryAndCity(categorySlug, citySlug),
    );

    return {
      coaches: coaches as CoachOverview[],
      category,
      citySlug,
      cityLabel,
      categorySlug,
      page,
    };
  },
  head: ({ params, loaderData }) => {
    const { category, cityLabel, page } = loaderData || {};
    const categoryName =
      category?.listingName || category?.name || params.categorySlug;
    const cityName = cityLabel || params.slug;
    const pageUrl = `https://dreamcoachmatch.com/coaches/city/${params.slug}/category/${params.categorySlug}`;
    const requestedPatternTitle = `Find ${categoryName} Coaches in ${cityName} at DreamCoachMatch`;
    const pageSeoTitle =
      "string" === typeof page?.seoTitle && 0 < page.seoTitle.trim().length
        ? page.seoTitle
        : undefined;
    const metaTitle = pageSeoTitle ?? requestedPatternTitle;
    const fallbackDescription = `Find expert ${categoryName} coaches specializing in various fields in ${cityName}. Connect with top professionals for personal and professional growth. Dream Coach Match helps you find your ideal coach in ${cityName} for ${categoryName}.`;
    const pageMetaDescription =
      "string" === typeof page?.metaDescription &&
      0 < page.metaDescription.trim().length
        ? page.metaDescription
        : undefined;
    const description = pageMetaDescription ?? fallbackDescription;
    const ogImage = `https://dreamcoachmatch.com/img/og/city-${params.slug}.jpg`;
    const keywords = [
      `${categoryName} coach`,
      `${categoryName} coaching`,
      `${categoryName} coaching in ${cityName}`,
      `coaches in ${cityName}`,
      `${cityName} coaching`,
      "Dream Coach Match",
    ];

    const metaConfig = createMetaTags({
      title: metaTitle,
      description,
      ogTitle: metaTitle,
      ogDescription: description,
      ogImage,
      ogUrl: pageUrl,
      twitterCard: "summary_large_image",
      twitterTitle: metaTitle,
      twitterDescription: description,
      twitterImage: ogImage,
      canonical: pageUrl,
      keywords,
      author: "Dream Coach Match",
    });

    return {
      title: metaTitle,
      ...metaConfig,
    };
  },
  component: CityCategoryCoachesPage,
});

function CityCategoryCoachesPage() {
  const { coaches, category, citySlug, cityLabel, categorySlug, page } =
    Route.useLoaderData();

  const categoryName = category?.listingName || category?.name || "Specialized";
  const cityName = cityLabel || citySlug;
  const pageTitle = `${categoryName} Coaches in ${cityName}`;
  const combinedBreakdownName = `${categoryName} in ${cityName}`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <Heading
        preHeadline="Dream Coach Match"
        headline={pageTitle}
        subheadline={`Find specialized ${categoryName} coaches in ${cityName} to help you achieve your personal and professional goals.`}
        isH1
      />
      <BreakdownIntro
        breakdownName={combinedBreakdownName}
        breakdownTypeSlug={`${categorySlug}-in-${citySlug}`}
        ctaButtonTextTemplate="Use our Matchmaker to Find Your Perfect Coach"
        utmCampaignPrefix="city_category_coaches"
      />
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {coaches.length > 0 ? (
          coaches.map((coach, i) => (
            <React.Fragment key={coach.id}>
              <CoachCard
                coachData={{
                  coach: {
                    id: coach.id,
                    username: coach.username,
                    fullName: coach.fullName,
                    membershipPlan: coach.membershipPlan,
                    profilePictureUrl: coach.profilePictureUrl,
                    isFeaturedCoach: coach.isFeaturedCoach,
                    isFoundingCoach: coach.isFoundingCoach,
                  },
                  profile: {
                    coachingTitle: coach.coachingTitle,
                    brandLogoUrl: coach.brandLogoUrl,
                    brandName: coach.brandName,
                    title: coach.title,
                  },
                }}
              />
              {i === 11 && (
                <MidListCTA
                  breakdownName={combinedBreakdownName}
                  breakdownTypeSlug={`${categorySlug}-in-${citySlug}`}
                  preLinkText="Need help choosing?"
                  ctaLinkTextTemplate="Let us match you with the ideal {} coach in {}!"
                  utmCampaignPrefix="city_category_coaches_mid"
                />
              )}
            </React.Fragment>
          ))
        ) : (
          <p className="col-span-full text-center text-lg text-gray-500">
            {`No ${categoryName} coaches found in ${cityName} yet.`}
          </p>
        )}
      </div>
      <BottomCTA
        itemCount={coaches.length}
        itemTypePlural="coaches"
        breakdownName={combinedBreakdownName}
        breakdownTypeSlug={`${categorySlug}-in-${citySlug}`}
        joinAsLinkUrl="/page/coach"
        joinAsLinkText="Join as a coach."
        mainCtaSubTextTemplate={`Ready to connect with a specialized ${categoryName} coach in ${cityName}?`}
        mainCtaButtonTextTemplate="Try our free Matchmaker tool"
        utmCampaignPrefix="city_category_coaches_bottom"
      />
      <div className="mx-auto max-w-3xl">
        <BlogGrid
          limit={2}
          className="mt-8"
          query={{ "data.tags": { $elemMatch: { $eq: categorySlug } } }}
          heading="Useful Related Articles"
        />
      </div>
      {page?.contentMd && (
        <div className="prose mx-auto mt-12">
          <MarkdownContent content={page.contentMd} />
        </div>
      )}
      {page?.faqItems && page.faqItems.length > 0 && (
        <FaqList items={page.faqItems} className="mx-auto max-w-2xl" />
      )}
    </div>
  );
}
